import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'

import { Modal, ModalProps } from '@themenu/design-system'

import { useSurveyAnswer } from 'providers/corporate-surveys/useSurveyAnswer'

import { IntroductionStep } from './steps/IntroductionStep'
import { RecardingPreferenceQuestionStep } from './steps/RecardingPreferenceQuestionStep'
import { SuccessStep } from './steps/SuccessStep'

import { useRecardingPreferenceSurveyStore } from './store'

import { getModalTitle } from './helpers/getModalTitle'
import { useCorporateConfig } from 'providers/corporate/useCorporateConfig'
import { useEffect, useMemo } from 'react'

import { useLocalStorage } from 'hooks/useLocalStorage'
import { eventSenderManager } from 'lib/analytics/eventSender'

dayjs.extend(isBetween)

type RecardingPreferenceSurveyProps = Pick<ModalProps, 'isOpen' | 'onClose'>

export const RECARDING_PREFERENCE_SURVEY_CLOSED_TIMES_KEY =
  'recarding-preference-survey-closed-times'

export const RecardingPreferenceSurvey = ({
  isOpen,
  onClose
}: RecardingPreferenceSurveyProps) => {
  const { data: surveyAnswer, isLoading: isLoadingSurvey } = useSurveyAnswer({
    surveyType: 'RECARDING_PREFERENCE'
  })

  const [closedTimes, setClosedTimes] = useLocalStorage(
    RECARDING_PREFERENCE_SURVEY_CLOSED_TIMES_KEY,
    0
  )

  const { isLoading: isLoadingConfig, data: config } = useCorporateConfig()
  const isRecardingEnabled = config?.showRecardingModules

  const recardingSurveyInitialDate = config?.recardingSurveyInitialDate
  const isInRangeOfSurveyDate = dayjs().isBetween(
    recardingSurveyInitialDate,
    dayjs(recardingSurveyInitialDate).add(30, 'days'),
    'day',
    '[]'
  )

  const { step } = useRecardingPreferenceSurveyStore()

  const handleCloseSurvey = () => {
    const newClosedTimes = closedTimes + 1

    setClosedTimes(newClosedTimes)
    eventSenderManager.sendEvent({
      label: 'recarding preference survey closed',
      metadata: {
        closedTimes: newClosedTimes
      }
    })
    onClose?.()
  }

  const shouldShowSurvey = useMemo(() => {
    if (
      isLoadingConfig ||
      isLoadingSurvey ||
      isRecardingEnabled ||
      !isInRangeOfSurveyDate ||
      surveyAnswer?.status !== 404 ||
      surveyAnswer?.data?.response !== undefined ||
      closedTimes >= 3
    ) {
      return false
    }

    return true
  }, [
    isLoadingConfig,
    isLoadingSurvey,
    isRecardingEnabled,
    surveyAnswer,
    isInRangeOfSurveyDate,
    closedTimes
  ])

  useEffect(() => {
    if (shouldShowSurvey) {
      eventSenderManager.sendEvent({
        label: 'recarding preference survey shown',
        metadata: {
          surveyType: 'RECARDING_PREFERENCE'
        }
      })
    }
  }, [shouldShowSurvey])

  useEffect(() => {
    if (shouldShowSurvey) {
      eventSenderManager.sendEvent({
        label: 'recarding preference survey step changed',
        metadata: {
          step
        }
      })
    }
  }, [shouldShowSurvey, step])

  if (!shouldShowSurvey) {
    return null
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseSurvey}
      srTitle="Pesquisa sobre a troca de cartões Swile"
      title={getModalTitle(step)}
      maxWidth="none"
      width={540}
      className="p-24 mx-8"
      actionLabels={{ close: 'Fechar' }}
    >
      {step === 'introduction' && <IntroductionStep />}
      {step === 'recarding-preference-question' && (
        <RecardingPreferenceQuestionStep />
      )}
      {step === 'success' && <SuccessStep onFinish={onClose} />}
    </Modal>
  )
}
